/**
 * Alarms store — Context + provider holding the tenant's alarm rules
 * and active event log. Backs both the human-facing Rules-tab editor
 * (which becomes real in B0.42 — was a disabled placeholder) and the
 * copilot's alarm-rule authoring tools (B0.43).
 *
 * Mirrors PlantProvider + DashboardsProvider. Persists to localStorage
 * `divineiot:alarms` so user-authored rules survive reload.
 *
 * Phase 1 swaps storage for /v1/iot/alarms/rules + /v1/iot/alarms/events
 * (already shipped server-side in Arun's M3 — see api/openapi.yaml). The
 * React surface stays identical.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AlarmItem } from "@/lib/widgets";
import { FX_ALARMS } from "@/lib/widgets/__fixtures";
import {
  useAlarmEventsQuery,
  useAlarmRulesQuery,
  toAlarmItem,
  toAlarmRule,
  toRuleCreateBody,
  toRulePatchBody,
  backendErrorMessage,
  isLiveApi,
  alarms as alarmsApi,
} from "@/lib/api";
import { useToast } from "@/lib/ui/toast";

const ALARM_RULES_KEY = ["alarm-rules"] as const;

const STORAGE_KEY = "divineiot:alarms";

/**
 * Local alarm-rule shape — mirrors the server-side `AlarmRule` schema
 * in api/openapi.yaml:1533. Kept client-side-friendly:
 *   - `targetTagId` is the tag id the rule fires on (resolved against
 *     the catalog at validate time)
 *   - `condition` is the MVP `{op, value}` grammar
 *   - `triggers7d` is a counter the engine updates; the editor surfaces it
 */
export interface AlarmRule {
  id: string;
  name: string;
  targetTagId: string;
  /** Human-readable target — used in the card subtitle (e.g. `furnace_1.bath_temp`). */
  targetLabel?: string;
  condition: {
    op: "gt" | "lt" | "gte" | "lte" | "eq" | "ne" | "spc";
    value: number | string;
    /** Optional "for X seconds" debounce (B0.43 — copilot can author). */
    forSeconds?: number;
    /** S15, op "spc": the control chart of the target tag whose new live signals fire the rule. */
    chartId?: string;
    /** S15, op "spc": the chart rules that fire it (we1 to we4, ewma, cusum_upper, cusum_lower); none: any. */
    rules?: string[];
  };
  severity: "info" | "warning" | "critical";
  channels: string[]; // "email" | "in-app" | "sms" | "slack" | "webhook"
  enabled: boolean;
  triggers7d: number;
  createdAt: string;
  updatedAt: string;
}

export class AlarmsEditError extends Error {
  readonly code: "not_found" | "invalid";
  constructor(code: "not_found" | "invalid", message: string) {
    super(message);
    this.code = code;
    this.name = "AlarmsEditError";
  }
}

/**
 * Phase-0 seed rules — lifted from the previous SAMPLE_RULES inline
 * constant in alarms.tsx so the Rules tab still has content on first
 * load even without authoring anything.
 */
const SEED_RULES: AlarmRule[] = [
  {
    id: "rule-1",
    name: "Bath temperature high",
    targetTagId: "tag-furn1-bath-temp",
    targetLabel: "furnace_*.bath_temp",
    condition: { op: "gt", value: 1500, forSeconds: 30 },
    severity: "critical",
    channels: ["email", "in-app"],
    enabled: true,
    triggers7d: 3,
    createdAt: "2026-05-01T00:00:00.000Z",
    updatedAt: "2026-05-20T00:00:00.000Z",
  },
  {
    id: "rule-2",
    name: "Sand moisture out of band",
    targetTagId: "tag-sand-moisture",
    targetLabel: "sand_mixer_*.moisture",
    condition: { op: "gt", value: 4.2 },
    severity: "warning",
    channels: ["in-app"],
    enabled: true,
    triggers7d: 8,
    createdAt: "2026-05-05T00:00:00.000Z",
    updatedAt: "2026-05-22T00:00:00.000Z",
  },
  {
    id: "rule-3",
    name: "Pour temperature low",
    targetTagId: "tag-pour-temperature",
    targetLabel: "pour_station_*.pour_temperature",
    condition: { op: "lt", value: 1360, forSeconds: 10 },
    severity: "critical",
    channels: ["email", "in-app", "sms"],
    enabled: true,
    triggers7d: 1,
    createdAt: "2026-05-08T00:00:00.000Z",
    updatedAt: "2026-05-22T00:00:00.000Z",
  },
  {
    id: "rule-4",
    name: "Gateway offline",
    targetTagId: "gateway-heartbeat",
    targetLabel: "gateway:*",
    condition: { op: "gt", value: "5m" },
    severity: "critical",
    channels: ["email"],
    enabled: true,
    triggers7d: 1,
    createdAt: "2026-04-15T00:00:00.000Z",
    updatedAt: "2026-04-15T00:00:00.000Z",
  },
];

export interface AlarmsState {
  rules: AlarmRule[];
  events: AlarmItem[];
  /** The first read of the events is on its way: an empty list says nothing yet. */
  eventsLoading: boolean;
  /** Mutation counter — drives memoised consumers. */
  version: number;

  ruleById: (id: string) => AlarmRule | undefined;

  /** Create a new rule. Returns its id. */
  createRule: (
    rule: Omit<AlarmRule, "id" | "createdAt" | "updatedAt" | "triggers7d"> & {
      triggers7d?: number;
    },
  ) => string;

  /** Patch a rule. Throws if id is unknown. */
  updateRule: (id: string, patch: Partial<AlarmRule>) => void;

  /** Delete a rule. Throws if id is unknown. */
  deleteRule: (id: string) => void;

  /** Toggle the enabled flag. */
  toggleRule: (id: string) => void;

  /** Acknowledge an event. */
  ackEvent: (eventId: string) => void;

  /** Reset to the seed (drops all user-authored rules). */
  reset: () => void;
}

const AlarmsContext = createContext<AlarmsState | null>(null);

export function useAlarms(): AlarmsState {
  const ctx = useContext(AlarmsContext);
  if (!ctx) throw new Error("useAlarms must be used inside <AlarmsProvider>");
  return ctx;
}

export function useOptionalAlarms(): AlarmsState | null {
  return useContext(AlarmsContext);
}

interface Persisted {
  rules: AlarmRule[];
  events: AlarmItem[];
}

function readPersisted(): Persisted | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      !Array.isArray(parsed.rules) ||
      !Array.isArray(parsed.events)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writePersisted(p: Persisted) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* swallow */
  }
}

export function AlarmsProvider({ children }: { children: ReactNode }) {
  // Live mode: start empty + let the org-scoped queries populate. Seed +
  // global localStorage are stub-mode only (they leaked one org's rules/
  // events into the next).
  const [{ rules, events }, setData] = useState<Persisted>(
    () =>
      isLiveApi
        ? { rules: [], events: [] }
        : (readPersisted() ?? { rules: SEED_RULES, events: FX_ALARMS }),
  );
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!isLiveApi) writePersisted({ rules, events });
  }, [rules, events]);

  const bumpVersion = useCallback(() => setVersion((v) => v + 1), []);

  // ── B1.0 live-API wiring ──────────────────────────────────────────
  // Two independent queries: events (the alarm list) + rules (Rules tab).
  // Each effect overwrites only its own branch of the {rules, events}
  // shape. Refetch clobbers local rule authoring; B1.1 wraps mutations
  // in useMutation. With VITE_USE_LIVE_API unset both queries are
  // disabled and the provider stays on seed data.
  const eventsQuery = useAlarmEventsQuery();
  const rulesQuery = useAlarmRulesQuery();
  const toast = useToast();
  const qc = useQueryClient();
  const erroredRef = useRef(false);
  const [eventsRead, setEventsRead] = useState(false);

  // Live mode: re-pull the org's rules after a write so the list reflects
  // server truth (the rule engine + any server-side normalisation). The
  // useAlarmRulesQuery effect below maps the refetched DTOs back into state.
  const refetchRules = useCallback(
    () => void qc.invalidateQueries({ queryKey: ALARM_RULES_KEY }),
    [qc],
  );

  useEffect(() => {
    const data = eventsQuery.data;
    if (data) {
      setData((prev) => ({ ...prev, events: data.items.map(toAlarmItem) }));
      setEventsRead(true);
      bumpVersion();
    }
  }, [eventsQuery.data, bumpVersion]);

  useEffect(() => {
    const data = rulesQuery.data;
    if (data) {
      setData((prev) => ({ ...prev, rules: data.items.map(toAlarmRule) }));
      bumpVersion();
    }
  }, [rulesQuery.data, bumpVersion]);

  useEffect(() => {
    const err = eventsQuery.error ?? rulesQuery.error;
    if (err && !erroredRef.current) {
      erroredRef.current = true;
      toast.push("error", backendErrorMessage(err));
    }
  }, [eventsQuery.error, rulesQuery.error, toast]);
  // ──────────────────────────────────────────────────────────────────

  const ruleById = useCallback(
    (id: string) => rules.find((r) => r.id === id),
    [rules],
  );

  const createRule = useCallback<AlarmsState["createRule"]>(
    (rule) => {
      const id = `rule-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const now = new Date().toISOString();
      const next: AlarmRule = {
        ...rule,
        id,
        triggers7d: rule.triggers7d ?? 0,
        createdAt: now,
        updatedAt: now,
      };
      // Optimistic local insert — keeps the synchronous `id` return the
      // copilot tools (lib/copilot/tools.ts) depend on, and the UI updates
      // instantly. Live mode then POSTs + refetches to reconcile with the
      // server-minted row (real UUID, server defaults).
      setData((prev) => ({ ...prev, rules: [next, ...prev.rules] }));
      bumpVersion();
      if (isLiveApi) {
        void alarmsApi.rules
          .create(toRuleCreateBody(next))
          .then(() => refetchRules())
          .catch((err) => {
            // Roll the optimistic row back out so the list matches the server.
            setData((prev) => ({
              ...prev,
              rules: prev.rules.filter((r) => r.id !== id),
            }));
            bumpVersion();
            toast.push("error", backendErrorMessage(err, "rule not saved"));
          });
      }
      return id;
    },
    [bumpVersion, refetchRules, toast],
  );

  const updateRule = useCallback<AlarmsState["updateRule"]>(
    (id, patch) => {
      setData((prev) => {
        const existing = prev.rules.find((r) => r.id === id);
        if (!existing) {
          throw new AlarmsEditError("not_found", `Rule "${id}" not found.`);
        }
        return {
          ...prev,
          rules: prev.rules.map((r) =>
            r.id === id
              ? { ...r, ...patch, id, updatedAt: new Date().toISOString() }
              : r,
          ),
        };
      });
      bumpVersion();
      if (isLiveApi) {
        // toRulePatchBody drops target_tag_id (not patchable server-side) and
        // omits untouched fields so the server's COALESCE leaves them be.
        void alarmsApi.rules
          .update(id, toRulePatchBody(patch))
          .then(() => refetchRules())
          .catch((err) => {
            refetchRules(); // reconcile the optimistic patch back to server truth
            toast.push("error", backendErrorMessage(err, "change not saved"));
          });
      }
    },
    [bumpVersion, refetchRules, toast],
  );

  const deleteRule = useCallback<AlarmsState["deleteRule"]>(
    (id) => {
      setData((prev) => {
        if (!prev.rules.some((r) => r.id === id)) {
          throw new AlarmsEditError("not_found", `Rule "${id}" not found.`);
        }
        return { ...prev, rules: prev.rules.filter((r) => r.id !== id) };
      });
      bumpVersion();
      if (isLiveApi) {
        void alarmsApi.rules
          .remove(id)
          .then(() => refetchRules())
          .catch((err) => {
            refetchRules(); // failed delete → re-pull so the row reappears
            toast.push("error", backendErrorMessage(err, "rule not deleted"));
          });
      }
    },
    [bumpVersion, refetchRules, toast],
  );

  const toggleRule = useCallback<AlarmsState["toggleRule"]>(
    (id) => {
      let nextEnabled: boolean | undefined;
      setData((prev) => ({
        ...prev,
        rules: prev.rules.map((r) => {
          if (r.id !== id) return r;
          nextEnabled = !r.enabled;
          return { ...r, enabled: nextEnabled, updatedAt: new Date().toISOString() };
        }),
      }));
      bumpVersion();
      if (isLiveApi && nextEnabled !== undefined) {
        void alarmsApi.rules
          .update(id, { enabled: nextEnabled })
          .then(() => refetchRules())
          .catch((err) => {
            refetchRules(); // revert the optimistic flip to server truth
            toast.push("error", backendErrorMessage(err, "change not saved"));
          });
      }
    },
    [bumpVersion, refetchRules, toast],
  );

  const ackEvent = useCallback<AlarmsState["ackEvent"]>(
    (eventId) => {
      setData((prev) => ({
        ...prev,
        events: prev.events.map((e) =>
          e.id === eventId ? { ...e, acknowledged: true } : e,
        ),
      }));
      bumpVersion();
    },
    [bumpVersion],
  );

  const reset = useCallback(() => {
    setData({ rules: SEED_RULES, events: FX_ALARMS });
    bumpVersion();
  }, [bumpVersion]);

  // Through the one commit where the read has its data and the events are not set from it yet.
  const eventsLoading = eventsQuery.isLoading || (eventsQuery.isSuccess && !eventsRead);
  const value = useMemo<AlarmsState>(
    () => ({
      rules,
      events,
      eventsLoading,
      version,
      ruleById,
      createRule,
      updateRule,
      deleteRule,
      toggleRule,
      ackEvent,
      reset,
    }),
    [
      rules,
      events,
      eventsLoading,
      version,
      ruleById,
      createRule,
      updateRule,
      deleteRule,
      toggleRule,
      ackEvent,
      reset,
    ],
  );

  return (
    <AlarmsContext.Provider value={value}>{children}</AlarmsContext.Provider>
  );
}
